import { useAppInfiniteQuery } from "@/hooks/useAppInfiniteQuery"
import { useGetSearchParamsState } from "@/hooks/useGetSearchParams"
import { type GetPeoplesParams, getGetPeoplesQueryKey, getPeoples } from "@/networking/api"
import { STALE_TIME_FIVE_MINUTES_IN_MS } from "@/utils/constants"
import { extractNotNullFieldsFromObject, isNullOrUndefined, objectKeys } from "@/utils/ts-utils"
import type { PeopleFiltersParamsWithSearch } from "./useFilters"

type UseAppInfiniteQueryOptions = Parameters<
  typeof useAppInfiniteQuery<
    GetPeoplesParams,
    Awaited<ReturnType<typeof getPeoples>>["hydra:member"][number],
    unknown,
    ReturnType<typeof getGetPeoplesQueryKey>
  >
>[3]

// Cancel multiple queries while filter modifying
const getGetPeopleQueryKeyLocal = (params?: GetPeoplesParams) => {
  if (isNullOrUndefined(params)) return getGetPeoplesQueryKey()

  const p = extractNotNullFieldsFromObject(params)
  objectKeys(p).map(k => {
    if (k.startsWith("project.")) p[k] = undefined
  })
  return getGetPeoplesQueryKey(p)
}

export const useGetPeopleInfiniteQuery = (
  params?: GetPeoplesParams,
  filters?: PeopleFiltersParamsWithSearch[],
  options?: UseAppInfiniteQueryOptions
) => {
  const { getSearchParams, getOrderParams } = useGetSearchParamsState<GetPeoplesParams>()

  const queryParams = filters
    ? { ...getSearchParams, ...getOrderParams, ...params }
    : { ...getOrderParams, ...params }

  const {
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    flatData: people,
    totalCount: count,
  } = useAppInfiniteQuery(getPeoples, getGetPeoplesQueryKey, queryParams, {
    staleTime: STALE_TIME_FIVE_MINUTES_IN_MS,
    ...options,
  } as UseAppInfiniteQueryOptions)

  const isFetchingData =
    (isNullOrUndefined(options) || options.enabled !== false) && (isLoading || isFetchingNextPage)

  return { people, fetchNextPage, hasNextPage, isFetchingData, count }
}
